<template>
  <section class="password-section">
    <p class="title">
      <translate>
        Add a password to create an account, which lets you manage your booking
        and much more.
      </translate>
    </p>
    <template v-if="!alreadyHasAnAccount">
      <base-password
        v-model="newAccountPassword"
        autocomplete="new-password"
        name="password"
        :validation-rules="newPasswordValidationRules"
        :minlength="minimumPasswordLength"
        :maxlength="maximumPasswordLength"
        :required="passwordRequired"
        @update:model-value="$emit('update:modelValue', $event)"
      >
        <template #label>
          <translate> Password </translate>
        </template>
        <template #helpText>
          <translate
            :tt="{ minimumPasswordLength, suggestedMinimumPasswordLength }"
          >
            Case sensitive, {minimumPasswordLength}+ letters required
            ({suggestedMinimumPasswordLength} recommended)
          </translate>
        </template>
      </base-password>
    </template>
    <template v-else>
      <translate tag="p"> You already have an account. </translate>
      <login-modal :email="email">
        <translate tag="a" @click.prevent="$emit('login')">
          Log in or reset your password
        </translate>
      </login-modal>
    </template>
  </section>
</template>

<script lang="ts">
import * as z from 'zod'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'

@Component({
  inheritAttrs: false,
  emits: ['update:modelValue', 'login'],
})
class PasswordSection extends Vue {
  minimumPasswordLength = 6
  maximumPasswordLength = 128
  suggestedMinimumPasswordLength = 8

  @Prop({ required: false, default: false })
  passwordRequired: boolean

  @Prop({ required: false })
  email?: string

  @Prop({ required: true })
  alreadyHasAnAccount: boolean

  newAccountPassword: string = ''

  get newPasswordValidationRules() {
    if (this.passwordRequired) {
      return z
        .string({ required_error: 'required' })
        .min(this.minimumPasswordLength, { message: 'min' })
        .max(this.maximumPasswordLength, { message: 'max' })
    } else {
      return z
        .union([
          z
            .string()
            .min(this.minimumPasswordLength, { message: 'min' })
            .max(this.maximumPasswordLength, { message: 'max' }),
          z.string().length(0),
        ])
        .optional()
        .transform((e) => (e === '' ? undefined : e))
    }
  }
}

export default toNative(PasswordSection)
</script>

<style scoped>
.password-section {
  background-color: #eff6ff;
  margin: 2em -1.4em 0;
  padding: 1em 1.4em;
}

.title {
  color: var(--colour-dark-blue);
  font-size: var(--body);
  margin-bottom: 0.5em;
}
</style>
